import { EmployeesService } from '../../services/employees.service';
import { DeclarationService } from '../../services';
import { IDeclaration, IEmployee, IShift, ShiftService } from '../../services';
import { IHourCode, HourCodeService } from '../../services/hourCode.service';
import * as moment from "moment";

interface IResolve {
    wageCodes: IHourCode[];
    declaration: IDeclaration;
    refresh: boolean;
    employee: IEmployee;
}

class DeclarationController {

    private resolve: IResolve;
    private declaration: IDeclaration;
    private employee: IEmployee;
    private wageCodes: IHourCode[];
    private calendarStartDate: any;
    private calendarEndDate: any;
    private selectedHourCodeId: string; // Bindable property voor ng-model
    private shift: IShift;
    private showSleepShiftDeclarationType: boolean = false;

    constructor(
        private DeclarationService: DeclarationService,
        private EmployeesService: EmployeesService,
        private ShiftService: ShiftService,
        private HourCodeService: HourCodeService,
        private $state: ng.ui.IStateService,
        private $stateParams: ng.ui.IStateParamsService,
        private SweetAlert: any
    ) {
        'ngInject';
    }

    public $onInit = () => {
        this.declaration = this.resolve.declaration;
        this.employee = this.resolve.employee;

        console.log(this.employee)
        if (this.declaration) {
            // Bestaande declaratie: controleer of het oude of nieuwe systeem is
            this.declaration.dateTimeFrom = new Date(Date.parse(this.declaration.dateTimeFrom));
            this.declaration.dateTimeTo = new Date(Date.parse(this.declaration.dateTimeTo));
            
            // Debug: log invoiced status bij initialisatie
            console.log('[Declaration] Initialized with invoiced status:', {
                id: this.declaration.id,
                invoiceId: this.declaration.invoiceId,
                sendToCootje: this.declaration.sendToCootje
            });
            
            // Initialiseer selectedHourCodeId voor backward compatibility
            this.selectedHourCodeId = this.declaration.hourCodeId || this.declaration.wageCodeId;
            
            // Als het een oude declaratie is (alleen wageCodeId), behoud deze
            // Als het een nieuwe declaratie is (hourCodeId), gebruik deze
            // Beide kunnen naast elkaar bestaan tijdens migratie
        } else {
            // Nieuwe declaratie: gebruik hourCodeId
            this.declaration = new IDeclaration();
            this.declaration.dateTimeFrom = this.$stateParams['shiftStartDate'];
            this.declaration.dateTimeTo = this.$stateParams['shiftEndDate'];
            this.declaration.shiftId = this.$stateParams['shiftId'];
            this.declaration.employeeId = this.$stateParams['employeeId'];
            this.declaration.registrationType = this.$stateParams['type'];
            this.declaration.status = 0;
            this.selectedHourCodeId = null;
            this.declaration.isSleepShiftActiveWork = false;
        }

        if (this.declaration.registrationType == '1' && this.declaration.shiftId) {
            this.wageCodes = this.resolve.wageCodes || [];
            this.loadSleepShiftContext();
        } else if (this.declaration.registrationType == '2' && this.declaration.shiftId) {
            this.loadExpenseHourCodes();
        } else {
            // Voor tijd declaraties (type = 1), gebruik de looncomponent hourcodes uit resolve
            this.wageCodes = this.resolve.wageCodes || [];
        }
    }

    private loadSleepShiftContext = () => {
        if (!this.declaration.shiftId || this.employee.zzp) {
            this.showSleepShiftDeclarationType = false;
            return;
        }

        this.ShiftService.get(this.declaration.shiftId).then((shift: IShift) => {
            this.shift = shift;
            this.showSleepShiftDeclarationType = !!(shift.isSleepShift && shift.sleepShiftOrtRulesActive);
            if (!this.showSleepShiftDeclarationType) {
                this.declaration.isSleepShiftActiveWork = false;
            } else if (this.declaration.isSleepShiftActiveWork === undefined || this.declaration.isSleepShiftActiveWork === null) {
                this.declaration.isSleepShiftActiveWork = false;
            }
        });
    }

    private loadExpenseHourCodes = () => {
        console.log('[Declaration] Laden van onkosten hourcodes', { shiftId: this.declaration.shiftId });
        
        if (!this.declaration.shiftId) {
            console.log('[Declaration] Geen shiftId beschikbaar');
            this.wageCodes = [];
            return;
        }
        
        // Haal shift op via ShiftService - de CAO is nu al in de shift response
        this.ShiftService.get(this.declaration.shiftId).then((shift: IShift) => {
            console.log('[Declaration] Shift opgehaald:', { 
                shiftId: shift.id, 
                teamLocationId: shift.teamLocationId, 
                teamLocationAccountId: shift.teamLocationAccountId,
                teamLocationSalaryScaleGroupId: shift.teamLocationSalaryScaleGroupId,
                teamLocationSalaryScaleGroupName: shift.teamLocationSalaryScaleGroupName
            });
            
            if (!shift) {
                console.log('[Declaration] Geen shift gevonden');
                this.wageCodes = [];
                return;
            }
            
            // Gebruik de CAO direct uit de shift response
            if (shift.teamLocationSalaryScaleGroupId) {
                const salaryScaleGroupId = shift.teamLocationSalaryScaleGroupId;
                const caoSource = 'Shift (Location/Account)';
                console.log('[Declaration] CAO gevonden in shift response:', { 
                    salaryScaleGroupId: salaryScaleGroupId, 
                    salaryScaleGroupName: shift.teamLocationSalaryScaleGroupName,
                    source: caoSource
                });
                this.loadHourCodesByCao(salaryScaleGroupId, caoSource);
            } else {
                console.log('[Declaration] Geen CAO beschikbaar in shift response:', { shift: shift });
                this.wageCodes = [];
            }
        }).catch((error) => {
            console.error('[Declaration] Fout bij ophalen Shift:', error);
            this.wageCodes = [];
        });
    }

    private loadHourCodesByCao = (salaryScaleGroupId: string, caoSource: string) => {
        console.log('[Declaration] Ophalen hourcodes voor CAO:', { salaryScaleGroupId: salaryScaleGroupId, caoSource: caoSource });
        this.HourCodeService.getBySalaryScaleGroupId(salaryScaleGroupId).then((hourCodes: IHourCode[]) => {
            console.log('[Declaration] Alle hourcodes opgehaald:', { 
                total: hourCodes.length, 
                hourCodes: hourCodes.map(hc => ({ id: hc.id, code: hc.code, name: hc.name, type: hc.type }))
            });
            
            // Filter alleen onkosten (type = 1)
            this.wageCodes = hourCodes.filter(hc => hc.type === 1);
            
            console.log('[Declaration] Gefilterde onkosten hourcodes:', { 
                total: this.wageCodes.length, 
                wageCodes: this.wageCodes.map(hc => ({ id: hc.id, code: hc.code, name: hc.name, type: hc.type }))
            });
        }).catch((error) => {
            console.error('[Declaration] Fout bij ophalen hourcodes:', error);
            this.wageCodes = [];
        });
    }

    roundMinutes(date) {
        var coeff = 1000 * 60 * 15;
        return new Date(Math.round(date.getTime() / coeff) * coeff);
    }

    startDateBeforeRender($dates) {
    }

    endDateBeforeRender($view, $dates) {
        if (this.declaration.dateTimeFrom) {
            var activeDate = moment(this.declaration.dateTimeFrom).subtract(1, $view).add(1, 'minute');

            $dates.filter(function (date) {
                return date.localDateValue() <= activeDate.valueOf();
            }).forEach(function (date) {
                date.selectable = false;
            });
        }
    }

    submitDeclaration() {
        if (this.declaration.registrationType == '1') {
            var duration = moment.duration(moment(this.declaration.dateTimeTo).diff(moment(this.declaration.dateTimeFrom))).asHours();

            this.declaration.dateTimeFrom = moment(this.declaration.dateTimeFrom).format("YYYY-MM-DD HH:mm");
            this.declaration.dateTimeTo = moment(this.declaration.dateTimeTo).format("YYYY-MM-DD HH:mm");

            if (duration > 6) {
                const self = this; // Behoud context
                this.SweetAlert.swal({
                    title: 'Let op!',
                    text: 'In geval van pauzes, maak 2 declaraties aan.',
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#2ECC71',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Ja',
                    cancelButtonText: 'Nee, annuleren',
                }, (isConfirm) => {
                    if (isConfirm) {
                        // Check invoiced status na eerste swal
                        // Wacht even zodat de eerste swal volledig is gesloten voordat we de tweede tonen
                        console.log('[Declaration] First swal confirmed, checking invoiced status');
                        setTimeout(() => {
                            self.checkInvoicedAndSave();
                        }, 300); // 300ms delay om te zorgen dat de eerste swal volledig is gesloten
                    } else { 
                        console.log('[Declaration] First swal cancelled');
                    }
                });
            } else {
                // Geen duration check, direct check invoiced status
                console.log('[Declaration] Duration <= 6, checking invoiced status directly');
                this.checkInvoicedAndSave();
            }
        } else {
            // Validatie: minstens één moet zijn ingesteld (backward compatible)
            const wageCodeId = this.getWageCodeId();
            if (!wageCodeId) {
                this.SweetAlert.swal({
                    title: 'Let op!',
                    text: 'Vul een type in',
                    type: 'warning',
                    confirmButtonColor: '#2ECC71',
                    confirmButtonText: 'Ok',
                });
                return;
            }
            
            this.declaration.dateTimeFrom = null;
            this.declaration.dateTimeTo = null;
            
            // Gebruik helper method voor code retrieval
            const code = this.getWageCodeCode();
            if (code == '5091' || code == '5090') {
                this.declaration.value = 1;
            }
            
            // Voor nieuwe declaraties: gebruik hourCodeId
            if (!this.declaration.id) {
                // Nieuwe declaratie: zet hourCodeId, clear wageCodeId
                if (this.declaration.hourCodeId) {
                    // Al ingesteld via wageCodeUpdated
                } else if (this.declaration.wageCodeId) {
                    // Fallback: converteer wageCodeId naar hourCodeId
                    this.declaration.hourCodeId = this.declaration.wageCodeId;
                    this.declaration.hourCodeCode = this.declaration.wageCodeCode || this.getWageCodeCode();
                    this.declaration.wageCodeId = null;
                    this.declaration.wageCodeCode = null;
                }
            }
            // Voor bestaande declaraties: behoud wageCodeId voor backward compatibility
            
            this.checkInvoicedAndSave();
        }
        
    }

    private checkInvoicedAndSave() {
        // Check of declaratie gefactureerd is
        const isInvoiced = this.declaration.invoiceId || this.declaration.sendToCootje;
        
        console.log('[Declaration] checkInvoicedAndSave called:', {
            declarationId: this.declaration.id,
            invoiceId: this.declaration.invoiceId,
            sendToCootje: this.declaration.sendToCootje,
            isInvoiced: isInvoiced,
            forceRecalculate: this.declaration.forceRecalculate
        });
        
        if (isInvoiced && !this.declaration.forceRecalculate) {
            // Toon waarschuwing
            console.log('[Declaration] Showing invoiced warning modal');
            const self = this; // Behoud context voor callback
            this.SweetAlert.swal({
                title: 'Waarschuwing!',
                html: 'Deze declaratie is al gefactureerd. ' +
                      'Als u deze toch wilt herberekenen, kan dit gevolgen hebben voor de factuur. ' +
                      'Weet u zeker dat u door wilt gaan?',
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Ja, toch herberekenen',
                cancelButtonText: 'Annuleren',
            }, (isConfirm) => {
                console.log('[Declaration] Invoiced warning modal result:', isConfirm);
                if (isConfirm) {
                    // Zet forceRecalculate flag
                    self.declaration.forceRecalculate = true;
                    self.performSave();
                }
            });
        } else {
            console.log('[Declaration] Not invoiced or forceRecalculate is true, saving directly');
            this.performSave();
        }
    }

    saveDeclaration() {
        // Deze methode wordt nu alleen nog gebruikt voor backward compatibility
        // Gebruik checkInvoicedAndSave() in plaats daarvan
        this.checkInvoicedAndSave();
    }

    private performSave() {
        // Maak een kopie van de declaratie voor de update om te voorkomen dat beide codes worden meegestuurd
        const declarationToSave = Object.assign({}, this.declaration);
        
        // Altijd hourCodeId gebruiken als het aanwezig is (nieuwe versie)
        // Als hourCodeId aanwezig is, verwijder wageCodeId om validatie error te voorkomen
        if (declarationToSave.hourCodeId) {
            delete declarationToSave.wageCodeId;
            delete declarationToSave.wageCodeCode;
            delete declarationToSave.wageCodeName;
        } else if (declarationToSave.wageCodeId) {
            // Alleen voor backward compatibility: als alleen wageCodeId aanwezig is (oude declaratie die niet is aangepast)
            delete declarationToSave.hourCodeId;
            delete declarationToSave.hourCodeCode;
            delete declarationToSave.hourCodeName;
        }
        
        if (!this.declaration.id) {
            this.DeclarationService.post(declarationToSave).then((result: any) => {
                this.closeModal();
            });
        } else {
            this.DeclarationService.put(this.declaration.id, declarationToSave).then(result => {
                this.closeModal();
            });
        }
    }

    // Helper methods voor backward compatibility
    getWageCodeCode(): string {
        return this.declaration.hourCodeCode || this.declaration.wageCodeCode;
    }

    getWageCodeName(): string {
        return this.declaration.hourCodeName || this.declaration.wageCodeName;
    }

    getWageCodeId(): string {
        return this.declaration.hourCodeId || this.declaration.wageCodeId;
    }

    /**
     * Haalt de eenheid (value2) op van de geselecteerde hourcode, of retourneert "Aantal" als fallback
     */
    getUnitLabel(): string {
        if (!this.wageCodes || this.wageCodes.length === 0) {
            return 'Aantal';
        }
        
        // Probeer eerst via selectedHourCodeId (nieuwe selectie)
        let hourCodeId = this.selectedHourCodeId;
        
        // Als dat niet werkt, probeer via declaration.hourCodeId of wageCodeId (bestaande declaratie)
        if (!hourCodeId) {
            hourCodeId = this.declaration.hourCodeId || this.declaration.wageCodeId;
        }
        
        if (hourCodeId) {
            const selectedHourCode = this.wageCodes.find(w => w.id === hourCodeId);
            if (selectedHourCode && selectedHourCode.value2 && typeof selectedHourCode.value2 === 'string' && selectedHourCode.value2.trim() !== '') {
                return selectedHourCode.value2;
            }
        }
        
        return 'Aantal';
    }

    wageCodeUpdated() {
        var wageCode = this.wageCodes.find(w => w.id == this.selectedHourCodeId);
        if (wageCode) {
            // Altijd hourCodeId gebruiken (nieuwe versie)
            // Dit migreert automatisch oude declaraties naar het nieuwe systeem wanneer ze worden aangepast
            this.declaration.hourCodeId = wageCode.id;
            this.declaration.hourCodeCode = wageCode.code;
            this.declaration.hourCodeName = wageCode.name;
            // Verwijder oude wageCodeId om validatie error te voorkomen
            this.declaration.wageCodeId = null;
            this.declaration.wageCodeCode = null;
            this.declaration.wageCodeName = null;
        }
    }

    deleteDeclaration() {
        this.SweetAlert.swal({
            title: 'Let op!',
            text: 'Weet u zeker dat u deze declaration wilt verwijderen?',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d96557',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nee, annuleren',
        }, (isConfirm) => {
            if (isConfirm) {
                this.DeclarationService.delete(this.declaration.id).then(() => {
                    this.closeModal();
                });
            } else {

            }
        });
    }

    private closeModal() {
        if(this.resolve.refresh){
            this.$state.go('^', {"openShiftId": this.declaration.shiftId}, {reload: true});
        } else {
            this.$state.go('^', {"openShiftId": this.declaration.shiftId}, {reload: false});
        }
    }
}

export const declarationComponent = { 
    bindings: {
        resolve : "<",
        close: "&"
    },
    controller: DeclarationController,
    template: require('./declaration.html')
};



// WEBPACK FOOTER //
// ./src/app/modules/timesheets/declaration.component.ts